import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldAlert, ShieldCheck, Save, Sparkles } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import api, { authStore } from "../lib/api";

/**
 * /preferences — Partner Preferences panel.
 * Identity-gated: `identity_verified === true` required to save (Aadhaar OKYC).
 * Reads / writes users.partner_preferences via /api/preferences.
 */
const Field = ({ label, hint, children }) => (
  <div>
    <label className="input-label">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>{hint}</p>}
  </div>
);

const Input = (props) => <input {...props} className="underline-input" value={props.value ?? ""}/>;
const Select = ({ options, ...props }) => (
  <select {...props} className="underline-input" value={props.value ?? ""}>
    <option value="" disabled>Select…</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const TIER_COPY = {
  low:  { label: "Everyday · ₹250", desc: "Basic contact reveal for one profile." },
  mid:  { label: "Preferred · ₹1100", desc: "Full contact + family details reveal." },
  high: { label: "Premium · ₹3100", desc: "Contact + album + Kundali PDF included." },
};

export default function Preferences() {
  const nav = useNavigate();
  const [state, setState] = useState({ identity_verified: false, preferences: {} });
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!authStore.token()) { nav("/"); return; }
    (async () => {
      try {
        const { data } = await api.get("/preferences");
        setState({
          identity_verified: !!data.identity_verified,
          preferences: data.preferences || {},
        });
      } catch {
        toast.error("Failed to load preferences");
      } finally { setLoaded(true); }
    })();
  }, [nav]);

  const p = state.preferences;
  const set = (k) => (e) => {
    const val = e?.target ? e.target.value : e;
    setState((s) => ({ ...s, preferences: { ...s.preferences, [k]: val } }));
  };
  const setList = (k) => (e) => {
    const raw = e.target.value;
    const arr = raw.split(",").map((x) => x.trim()).filter(Boolean);
    setState((s) => ({ ...s, preferences: { ...s.preferences, [k]: arr } }));
  };
  const list = (k) => Array.isArray(p[k]) ? p[k].join(", ") : (p[k] || "");

  const save = async () => {
    if (!state.identity_verified) {
      toast.error("Complete Aadhaar OKYC to save preferences");
      return;
    }
    setBusy(true);
    try {
      const clean = Object.fromEntries(
        Object.entries(p).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      const { data } = await api.post("/preferences", clean);
      setState((s) => ({ ...s, preferences: data.preferences || {} }));
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!loaded) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--ivory)" }}>
      <p className="eyebrow">Loading preferences…</p>
    </div>;
  }

  return (
    <div data-testid="preferences-page" className="min-h-screen w-full flex flex-col" style={{ background: "var(--ivory)" }}>
      <header className="px-6 md:px-10 pt-6 pb-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--line)" }}>
        <BrandLogo size="sm"/>
        <button onClick={() => nav("/home")} data-testid="prefs-back" className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5" style={{ color: "var(--ash)" }}>
          <ArrowLeft size={14}/> Back to Home
        </button>
      </header>

      <section className="px-6 md:px-10 pt-10 pb-4">
        <div className="max-w-3xl mx-auto">
          <div className="eyebrow flex items-center gap-2"><Sparkles size={12}/> Partner Preferences</div>
          <h1 className="font-serif text-3xl md:text-4xl mt-2" style={{ color: "var(--crimson)" }}>
            Refine what you seek
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--ash)" }}>
            These filters power our matchmaking score. Update anytime — changes apply on your next match refresh.
          </p>

          {!state.identity_verified ? (
            <div data-testid="okyc-warning" className="mt-6 p-4 flex items-start gap-3" style={{ background: "rgba(122,14,24,0.05)", border: "1px solid var(--crimson)" }}>
              <ShieldAlert size={16} style={{ color: "var(--crimson)", marginTop: 2 }}/>
              <div>
                <p className="font-serif text-base" style={{ color: "var(--crimson)" }}>Aadhaar OKYC required</p>
                <p className="mt-1 text-sm" style={{ color: "var(--ash)" }}>
                  Preferences can be viewed but not saved until identity verification is complete. Please complete OKYC from the Home hub.
                </p>
              </div>
            </div>
          ) : (
            <div data-testid="okyc-verified" className="mt-6 p-3 inline-flex items-center gap-2 text-[11px] tracking-widest uppercase"
              style={{ background: "rgba(85,127,74,0.08)", border: "1px solid var(--leaf)", color: "var(--leaf)" }}>
              <ShieldCheck size={14}/> Identity verified · Preferences will be saved
            </div>
          )}
        </div>
      </section>

      <section className="flex-1 px-6 md:px-10 pt-4 pb-16">
        <div className="max-w-3xl mx-auto space-y-8">
          <div>
            <div className="eyebrow mb-4">Age & Physique</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Minimum age"><Input data-testid="p-min-age" type="number" min={18} max={80} value={p.min_age} onChange={set("min_age")}/></Field>
              <Field label="Maximum age"><Input data-testid="p-max-age" type="number" min={18} max={80} value={p.max_age} onChange={set("max_age")}/></Field>
              <Field label="Min height (cm)"><Input type="number" value={p.min_height_cm} onChange={set("min_height_cm")}/></Field>
              <Field label="Max height (cm)"><Input type="number" value={p.max_height_cm} onChange={set("max_height_cm")}/></Field>
            </div>
          </div>

          <div>
            <div className="eyebrow mb-4">Community</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Preferred castes" hint="comma separated"><Input data-testid="p-caste" value={list("caste")} onChange={setList("caste")}/></Field>
              <Field label="Gotra exclusions" hint="comma separated"><Input data-testid="p-gotra-exclude" value={list("gotra_exclude")} onChange={setList("gotra_exclude")}/></Field>
              <Field label="Preferred religions" hint="comma separated"><Input value={list("religion")} onChange={setList("religion")}/></Field>
              <Field label="Marital status" hint="comma separated"><Input value={list("marital_status")} onChange={setList("marital_status")}/></Field>
              <Field label="Manglik preference">
                <Select value={p.manglik} onChange={set("manglik")} options={["Any", "Only Non-Manglik", "Only Manglik", "Anshik OK"]}/>
              </Field>
              <Field label="Diet" hint="comma separated"><Input value={list("diet")} onChange={setList("diet")}/></Field>
            </div>
          </div>

          <div>
            <div className="eyebrow mb-4">Career & Location</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Education levels" hint="comma separated"><Input value={list("education")} onChange={setList("education")}/></Field>
              <Field label="Professions" hint="comma separated"><Input value={list("profession")} onChange={setList("profession")}/></Field>
              <Field label="Cities" hint="comma separated"><Input value={list("city")} onChange={setList("city")}/></Field>
              <Field label="States" hint="comma separated"><Input value={list("state")} onChange={setList("state")}/></Field>
            </div>
          </div>

          <div>
            <div className="eyebrow mb-4">Match-Box Budget</div>
            <p className="text-sm mb-4" style={{ color: "var(--ash)" }}>
              Sets the default unlock tier when you tap "Reveal contact" on a match. You can still override per-unlock.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="p-budget-tier-group">
              {["low", "mid", "high"].map((t) => {
                const active = (p.budget_tier || "").toLowerCase() === t;
                return (
                  <button key={t} type="button" data-testid={`p-tier-${t}`} onClick={() => set("budget_tier")(t)}
                    className="editorial-card p-4 text-left transition-all"
                    style={{ borderColor: active ? "var(--crimson)" : "var(--line)", background: active ? "rgba(122,14,24,0.04)" : "transparent" }}>
                    <p className="font-serif text-lg" style={{ color: active ? "var(--crimson)" : "var(--ink)" }}>{TIER_COPY[t].label}</p>
                    <p className="mt-1 text-xs" style={{ color: "var(--ash)" }}>{TIER_COPY[t].desc}</p>
                    {active && <span className="mt-3 inline-block text-[10px] tracking-widest uppercase" style={{ color: "var(--crimson)" }}>Selected</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="eyebrow mb-4">Guna Compatibility Floor</div>
            <p className="text-sm mb-4" style={{ color: "var(--ash)" }}>
              Only show profiles that score at least this Ashtakoota-weighted compatibility. Higher = stricter.
            </p>
            <div className="flex items-center gap-4">
              <input data-testid="p-min-compat" type="range" min={0} max={100} step={5}
                value={p.min_compat_pct ?? 0}
                onChange={(e) => set("min_compat_pct")(parseInt(e.target.value, 10))}
                className="flex-1"/>
              <span data-testid="p-min-compat-value" className="font-serif text-2xl" style={{ color: "var(--crimson)", minWidth: "3.5rem", textAlign: "right" }}>
                {p.min_compat_pct ?? 0}%
              </span>
            </div>
            <p className="mt-2 text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>
              Set to 0% to see everyone · 65%+ recommended for a curated feed
            </p>
          </div>

          <div>
            <div className="eyebrow mb-4">About your ideal partner</div>
            <textarea data-testid="p-about" value={p.about ?? ""} onChange={set("about")}
              rows={4} className="underline-input" style={{ resize: "vertical" }}
              placeholder="Two-three lines about the partner you seek."/>
          </div>

          <div className="pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-end gap-3" style={{ borderTop: "1px solid var(--line)" }}>
            {!state.identity_verified && (
              <p data-testid="prefs-save-caption" className="text-[11px] tracking-widest uppercase" style={{ color: "var(--crimson)" }}>
                Complete Aadhaar OKYC to enable saving
              </p>
            )}
            <button data-testid="prefs-save" onClick={save} disabled={busy || !state.identity_verified} className="btn-primary">
              {busy ? "Saving…" : "Save Preferences"} <Save size={14}/>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
